"use client";
import React, { createContext, useContext, useState, useEffect } from "react";
import { BASE_URL } from "../config/apiconfig";
const ContactContext = createContext();

export const useContact = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error("useContact must be used within a ContactProvider");
  }
  return context;
};

export const ContactProvider = ({ children }) => {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${BASE_URL}/contact`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 200 && result.data) {
        setContactData(result.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching contact data:", err);
      setError(err.message);

      // Fallback data in case of API failure
      setContactData({
        website: "https://manaprint.in",
        email: "manaprint6@gmail.com",
        phone: "+91 76608 27827",
        hours: "Mon–Sat, 10:00 AM – 7:00 PM IST",
        address: "Hyderabad, Telangana, India",
        enquiries: "business@manaprint.in",
        facebook: "#",
        twitter: "#",
        instagram: "https://www.instagram.com/manaprint_india/",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendContactMessage = async (formData) => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      const response = await fetch(
        "https://acsdev.in/manaprint_backend/public/api/client/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            message: formData.message,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 200) {
        setSubmitSuccess(true);
        return {
          success: true,
          message: result.message || "Your message has been sent successfully!",
        };
      } else {
        throw new Error(result.message || "Failed to send message");
      }
    } catch (err) {
      console.error("Error sending contact message:", err);
      setSubmitError(err.message);
      return {
        success: false,
        message: err.message || "Failed to send message. Please try again.",
      };
    } finally {
      setSubmitting(false);
    }
  };

  const clearSubmitStatus = () => {
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  useEffect(() => {
    fetchContactData();
  }, []);

  const value = {
    contactData,
    loading,
    error,
    submitting,
    submitError,
    submitSuccess,
    refetch: fetchContactData,
    sendContactMessage,
    clearSubmitStatus,
  };

  return (
    <ContactContext.Provider value={value}>{children}</ContactContext.Provider>
  );
};

export default ContactContext;
